/**
 * Building blocks shared by every settings pane:
 *
 *   <SettingsPane title="Notifications" description="Where alerts go">
 *     <SettingsSection title="Email" description="Alerts and digests by email">
 *       <SettingsRow label="Email alerts" hint="…"><Switch /></SettingsRow>
 *     </SettingsSection>
 *   </SettingsPane>
 *
 * A section is a bordered card; its rows are divided and put the label on
 * the left, the control on the right (stacked on narrow screens).
 */
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

/**
 * Settings buttons read as sentence case, not the app's mono uppercase (set
 * globally for every <button> in index.css), with slightly larger icons (16px
 * when the icon is the whole button). Put it on the settings container and on
 * each settings dialog (dialogs are portaled). Covers select triggers too.
 */
export const SETTINGS_BUTTONS = cn(
  "[&_button]:font-sans [&_button]:normal-case [&_button]:tracking-normal",
  "[&_[data-slot=button]]:font-medium",
  "[&_[data-slot=button]_svg]:h-[14px] [&_[data-slot=button]_svg]:w-[14px]",
  "[&_[data-slot=button]:has(>svg:only-child)_svg]:h-4 [&_[data-slot=button]:has(>svg:only-child)_svg]:w-4",
);

export function SettingsPane({ title, description, actions, children }: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold leading-tight tracking-tight">{title}</h2>
          {description && <p className="font-sans text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function SettingsSection({ title, description, icon, badge, actions, tone, children, className }: {
  title?: ReactNode;
  description?: ReactNode;
  /** A HugeIcons name, or a ready-made node such as a logo tile */
  icon?: string | ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  tone?: "danger";
  children?: ReactNode;
  className?: string;
}) {
  const hasHeader = title || description || actions;
  return (
    <section
      className={cn(
        "overflow-hidden rounded-lg border bg-card",
        tone === "danger" ? "border-destructive/30" : "border-border",
        className,
      )}
    >
      {hasHeader && (
        <header className={cn("flex items-start justify-between gap-4 px-5 py-4", children && "border-b", tone === "danger" ? "border-destructive/20" : "border-border")}>
          <div className="flex min-w-0 items-start gap-3">
            {icon && (typeof icon === "string" ? <LogoTile icon={icon} /> : icon)}
            <div className="min-w-0 space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className={cn("font-sans text-sm font-semibold", tone === "danger" && "text-destructive")}>{title}</h3>
                {badge}
              </div>
              {description && <p className="font-sans text-xs text-muted-foreground">{description}</p>}
            </div>
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      {children && <div className="divide-y divide-border">{children}</div>}
    </section>
  );
}

export function SettingsRow({ label, hint, children, stack, className }: {
  label: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
  /** Put the control under the label, full width (long inputs) */
  stack?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex gap-3 px-5 py-4",
        stack ? "flex-col" : "flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-6",
        className,
      )}
    >
      <div className="min-w-0 space-y-0.5">
        <div className="font-sans text-xs font-medium">{label}</div>
        {hint && <div className="font-sans text-xs text-muted-foreground">{hint}</div>}
      </div>
      {children && <div className={cn("flex items-center gap-2", !stack && "shrink-0")}>{children}</div>}
    </div>
  );
}

/** Plain padded block inside a section, for content that isn't a label/control pair */
export function SettingsBlock({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("px-5 py-4", className)}>{children}</div>;
}

// Brandfetch logo CDN client ID. Public by design (it's in every image URL),
// so it lives here; VITE_BRANDFETCH_CLIENT_ID overrides it.
const BRANDFETCH_CLIENT_ID =
  (import.meta as { env?: { VITE_BRANDFETCH_CLIENT_ID?: string } }).env?.VITE_BRANDFETCH_CLIENT_ID || "1idTWwF9qdgv2hzLScZ";

/**
 * A company's real logo from the Brandfetch logo CDN, in the same tile as
 * LogoTile. If the image fails it shows `fallbackIcon` instead.
 */
export function BrandLogo({ domain, name, fallbackIcon, className }: {
  domain: string;
  name: string;
  fallbackIcon: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!BRANDFETCH_CLIENT_ID || failed) return <LogoTile icon={fallbackIcon} className={className} />;
  return (
    <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40", className)}>
      <img
        src={`https://cdn.brandfetch.io/${domain}/w/72/h/72/icon?c=${BRANDFETCH_CLIENT_ID}`}
        alt={`${name} logo`}
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export function LogoTile({ icon, className }: { icon: string; className?: string }) {
  return (
    <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40", className)}>
      <Icon name={icon} size={17} />
    </div>
  );
}

/** Small status pill: a dot plus a word */
export function StatusPill({ tone, children, title }: {
  tone: "ok" | "warn" | "error" | "muted";
  children: ReactNode;
  title?: string;
}) {
  const dot = { ok: "bg-emerald-500", warn: "bg-amber-500", error: "bg-red-500", muted: "border border-muted-foreground/60" }[tone];
  const text = { ok: "text-foreground", warn: "text-amber-500", error: "text-red-500", muted: "text-muted-foreground" }[tone];
  return (
    <span title={title} className={cn("inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider", text)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {children}
    </span>
  );
}
